import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { LostPet, LostPetPublic, CreateLostPetData, LostPetFilters, ForumPostForLostPet } from '@/types/lost-pets';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/utils/logger';

export const useLostPets = (filters?: LostPetFilters) => {
  return useQuery({
    queryKey: ['lost-pets', filters],
    queryFn: async () => {
      // Utilise la vue publique sécurisée qui masque les coordonnées du propriétaire
      let query = supabase
        .from('lost_pets_public_safe')
        .select('*')
        .order('created_at', { ascending: false });

      // Filtre par statut (par défaut: actifs uniquement)
      if (filters?.status) {
        query = query.eq('status', filters.status);
      } else {
        query = query.eq('status', 'active');
      }

      if (filters?.species) {
        query = query.eq('species', filters.species);
      }
      
      if (filters?.breed) {
        query = query.ilike('breed', `%${filters.breed}%`);
      }
      
      if (filters?.colors) {
        query = query.ilike('colors', `%${filters.colors}%`);
      }
      
      if (filters?.location) {
        query = query.ilike('lost_location', `%${filters.location}%`);
      }
      
      if (filters?.dateFrom) {
        query = query.gte('lost_date', filters.dateFrom);
      }
      
      if (filters?.dateTo) {
        query = query.lte('lost_date', filters.dateTo);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      return data as LostPetPublic[];
    },
  });
};

// Hook pour récupérer le post forum associé à une fiche animal perdu
export const useForumPostForLostPet = (lostPetId: string) => {
  return useQuery({
    queryKey: ['forum-post-for-lost-pet', lostPetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('forum_posts')
        .select('id, title, created_at')
        .eq('lost_pet_id', lostPetId)
        .maybeSingle();
      
      if (error) throw error;
      return data as ForumPostForLostPet | null;
    },
    enabled: !!lostPetId,
  });
};

export const useLostPet = (id: string) => {
  return useQuery({
    queryKey: ['lost-pet', id],
    queryFn: async () => {
      // Utilise la vue publique sécurisée pour les détails
      const { data, error } = await supabase
        .from('lost_pets_public_safe')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data as LostPetPublic;
    },
  });
};

export const useMyLostPets = () => {
  return useQuery({
    queryKey: ['my-lost-pets'],
    queryFn: async () => {
      // Utilise la vue privée pour voir ses propres annonces avec toutes les données
      const { data, error } = await supabase
        .from('lost_pets_private')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as LostPet[];
    },
  });
};

export const useCreateLostPet = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (lostPetData: CreateLostPetData) => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      if (!user) throw new Error('Utilisateur non connecté');

      const { data, error } = await supabase
        .from('lost_pets')
        .insert({
          ...lostPetData,
          owner_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      // Publication automatique sur le forum avec liaison
      let forumPostCreated = false;
      try {
        const postTitle = `🚨 Animal perdu : ${lostPetData.name || lostPetData.species} à ${lostPetData.lost_location}`;
        const postContent = `Un animal a été signalé perdu.\n\n` +
          `📍 Lieu : ${lostPetData.lost_location}\n` +
          `📅 Date : ${new Date(lostPetData.lost_date).toLocaleDateString('fr-FR')}\n` +
          `🐾 Espèce : ${lostPetData.species}\n` +
          `${lostPetData.breed ? `Race : ${lostPetData.breed}\n` : ''}` +
          `${lostPetData.colors ? `Couleur(s) : ${lostPetData.colors}\n` : ''}` +
          `${lostPetData.distinctive_signs ? `Signes distinctifs : ${lostPetData.distinctive_signs}\n` : ''}` +
          `\n${lostPetData.personal_message || ''}\n\n` +
          `Consultez l'onglet Animaux Perdus pour plus de détails et contacter le propriétaire.`;

        const { error: forumError } = await supabase.from('forum_posts').insert({
          user_id: user.id,
          title: postTitle,
          content: postContent,
          category: 'lost_pets',
          lost_pet_id: data.id, // Liaison avec la fiche animal perdu
        });

        if (!forumError) {
          forumPostCreated = true;
        } else {
          logger.error('Erreur lors de la publication sur le forum:', forumError);
        }
      } catch (forumError) {
        logger.error('Erreur lors de la publication sur le forum:', forumError);
      }

      return { lostPet: data as LostPet, forumPostCreated };
    },
    onSuccess: ({ forumPostCreated }) => {
      queryClient.invalidateQueries({ queryKey: ['lost-pets'] });
      queryClient.invalidateQueries({ queryKey: ['my-lost-pets'] });
      queryClient.invalidateQueries({ queryKey: ['forum-posts'] });
      
      toast({
        title: '✅ Annonce publiée !',
        description: forumPostCreated 
          ? 'Votre fiche est visible dans "Animaux Perdus" et un post a été créé dans le forum communautaire.'
          : 'Votre fiche est visible dans "Animaux Perdus". (La publication forum n\'a pas pu être créée)',
      });
    },
    onError: (error) => {
      toast({
        title: 'Erreur',
        description: 'Impossible de créer l\'annonce. Veuillez réessayer.',
        variant: 'destructive',
      });
      logger.error('Erreur lors de la création de l\'annonce:', error);
    },
  });
};

export const useUpdateLostPet = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<LostPet> }) => {
      const { data, error } = await supabase
        .from('lost_pets')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as LostPet;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['lost-pets'] });
      queryClient.invalidateQueries({ queryKey: ['my-lost-pets'] });
      queryClient.invalidateQueries({ queryKey: ['lost-pet', data.id] });
      toast({
        title: 'Annonce mise à jour',
        description: 'Votre annonce a été mise à jour avec succès.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour l\'annonce. Veuillez réessayer.',
        variant: 'destructive',
      });
      logger.error('Erreur lors de la mise à jour de l\'annonce:', error);
    },
  });
}

export const useDeleteLostPet = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('lost_pets')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lost-pets'] });
      queryClient.invalidateQueries({ queryKey: ['my-lost-pets'] });
      toast({
        title: 'Annonce supprimée',
        description: 'Votre annonce a été supprimée avec succès.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer l\'annonce. Veuillez réessayer.',
        variant: 'destructive',
      });
      logger.error('Erreur lors de la suppression de l\'annonce:', error);
    },
  });
};

export const useMarkPetAsFound = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (lostPet: LostPetPublic) => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      if (!user) throw new Error('Utilisateur non connecté');

      // 1. Marquer l'alerte comme "found"
      const { error: updateError } = await supabase
        .from('lost_pets')
        .update({ 
          status: 'found',
        })
        .eq('id', lostPet.id);

      if (updateError) throw updateError;

      // 2. Créer le post forum de bonne nouvelle
      const postTitle = `🎉 Bonne nouvelle ! ${lostPet.name || 'Un animal'} a été retrouvé !`;
      const postContent = `L'animal ${lostPet.species}${lostPet.name ? ` "${lostPet.name}"` : ''} qui était perdu depuis le ${new Date(lostPet.lost_date).toLocaleDateString('fr-FR')} a été retrouvé !\n\nMerci à tous pour votre aide et votre mobilisation. 🐾❤️`;

      const { error: forumError } = await supabase.from('forum_posts').insert({
        user_id: user.id,
        title: postTitle,
        content: postContent,
        category: 'general',
      });

      if (forumError) {
        logger.error('Erreur lors de la publication sur le forum:', forumError);
      }

      return { forumPostCreated: !forumError };
    },
    onSuccess: ({ forumPostCreated }) => {
      queryClient.invalidateQueries({ queryKey: ['lost-pets'] });
      queryClient.invalidateQueries({ queryKey: ['my-lost-pets'] });
      queryClient.invalidateQueries({ queryKey: ['lost-pet'] });
      queryClient.invalidateQueries({ queryKey: ['forum-posts'] });
      
      toast({
        title: '🎉 Super nouvelle !',
        description: forumPostCreated 
          ? 'L\'annonce a été archivée et une publication a été créée sur le forum.'
          : 'L\'annonce a été archivée.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour l\'annonce. Veuillez réessayer.',
        variant: 'destructive',
      });
      logger.error('Erreur lors du marquage comme retrouvé:', error);
    },
  });
};